import React from 'react';
import { Target, Heart, Compass, Users, Shield, Award, TrendingUp, CheckCircle, Zap, Clock, Globe, FileCheck } from 'lucide-react';
import TestimonialsSlider from '../components/TestimonialsSlider';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const About = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="About Us - Canadian Trademark Services"
        description="Professional trademark registration specialists with 99% success rate. Experienced in CIPO procedures and Canadian IP law."
        keywords="about trademark services, professional CIPO registration, Canadian trademark specialists, intellectual property experts Canada"
      />
      {/* Hero Section */}
      <section className="relative pt-28 pb-24 overflow-hidden bg-gradient-to-br from-brand-600 to-brand-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c"
            alt="Professional team meeting"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <span className="text-white font-semibold text-sm">🍁 Established Canadian Trademark Practice</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About Trademark Filing Canada
            </h1>
            <p className="text-xl md:text-2xl text-brand-100 leading-relaxed">
              Professional trademark registration services with comprehensive CIPO expertise
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section - Image Left, Content Right */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-800 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1551836022-d5d88e9218df"
                alt="Professional business team"
                className="relative rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="bg-brand-100 rounded-full p-2">
                    <Shield className="w-5 h-5 text-brand-600" />
                  </div>
                  <span className="font-bold text-gray-900">1000+ Successful Registrations</span>
                </div>
                <p className="text-sm text-gray-600">Serving clients across all Canadian provinces</p>
              </div>
            </div>

            <div>
              <div className="inline-block bg-brand-100 rounded-full p-3 mb-4">
                <Target className="w-8 h-8 text-brand-600" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Professional Approach</h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Trademark Filing Canada provides professional trademark registration services for Canadian businesses seeking comprehensive intellectual property protection. Our systematic approach to CIPO applications combines thorough legal analysis with efficient administrative procedures.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                We maintain professional service standards throughout the registration process, ensuring clients receive expert guidance in trademark clearance, application preparation, and ongoing portfolio management.
              </p>
              <div className="bg-gradient-to-br from-brand-50 to-brand-100 rounded-xl p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-3xl font-bold text-brand-600 mb-1">99%</div>
                    <div className="text-sm text-gray-600">Success Rate</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-brand-600 mb-1">15+</div>
                    <div className="text-sm text-gray-600">Years Experience</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section - Content Left, Image Right */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-brand-100 text-brand-700 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Professional Distinction
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Specialized Canadian Trademark Practice
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Our practice focuses exclusively on Canadian trademark law and CIPO registration procedures. This specialized focus enables comprehensive knowledge of federal examination standards and administrative requirements.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4 bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
                  <div className="bg-brand-600 rounded-lg p-3 flex-shrink-0">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">CIPO Expertise</h3>
                    <p className="text-gray-600 text-sm">Comprehensive understanding of Canadian Intellectual Property Office examination criteria and procedural requirements.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
                  <div className="bg-brand-600 rounded-lg p-3 flex-shrink-0">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Systematic Process</h3>
                    <p className="text-gray-600 text-sm">Efficient application procedures with professional deadline management and responsive communications.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
                  <div className="bg-brand-600 rounded-lg p-3 flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Clear Communication</h3>
                    <p className="text-gray-600 text-sm">Transparent pricing structure and realistic timeline expectations throughout the registration process.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-400 to-brand-500 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1557804506-669a67965ba0"
                alt="Team collaboration"
                className="relative rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
              <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-500 rounded-full p-2">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Professional Service</div>
                    <div className="text-sm text-gray-600">Established Standards</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-brand-100 text-brand-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
              Service Principles
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Professional Standards</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Core principles guiding our Canadian trademark registration services
            </p>
          </div>

          <div className="md:grid md:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-brand-100 group min-w-full md:min-w-0 snap-center">
              <div className="bg-gradient-to-r from-brand-600 to-brand-800 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Client-Centered Service</h3>
              <p className="text-gray-600 leading-relaxed text-center">
                Professional service delivery prioritizing client objectives and business goals. Our systematic approach ensures comprehensive support throughout the trademark registration process, maintaining consistent communication and responsive case management.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-green-100 group min-w-full md:min-w-0 snap-center">
              <div className="bg-gradient-to-r from-green-600 to-green-800 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform">
                <Compass className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Professional Integrity</h3>
              <p className="text-gray-600 leading-relaxed text-center">
                Transparent fee structures, realistic timeline projections, and clear communication protocols. Professional service standards include comprehensive cost disclosure and honest assessment of application prospects throughout CIPO proceedings.
              </p>
            </div>

            <div className="bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-brand-100 group min-w-full md:min-w-0 snap-center">
              <div className="bg-gradient-to-r from-brand-600 to-brand-800 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Technical Excellence</h3>
              <p className="text-gray-600 leading-relaxed text-center">
                Maintaining superior standards in trademark clearance research, application preparation, and CIPO filing procedures. Professional excellence achieved through comprehensive knowledge of Canadian trademark law and systematic quality control protocols.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process/Approach - Image Left, Content Right */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1519389950473-47ba0277781c"
                alt="Professional team working"
                className="relative rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-brand-600 to-brand-800 text-white rounded-xl shadow-xl p-6 max-w-xs">
                <FileCheck className="w-8 h-8 mb-2" />
                <div className="font-bold text-lg">Systematic Approach</div>
                <div className="text-sm text-brand-100">1000+ successful registrations</div>
              </div>
            </div>

            <div>
              <div className="inline-block bg-brand-100 text-brand-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Professional Method
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Service Approach</h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Our systematic methodology ensures comprehensive support throughout the trademark registration process, maintaining professional standards and consistent success rates.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-brand-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0 shadow-lg">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Initial Evaluation</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Comprehensive assessment of trademark objectives including thorough clearance searches of CIPO databases and analysis of potential registration obstacles.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-brand-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0 shadow-lg">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Application Preparation</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Professional application drafting addressing all CIPO requirements with strategic classification selection and comprehensive documentation standards.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-brand-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0 shadow-lg">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Professional Management</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Systematic monitoring of application status with responsive handling of examiner communications and coordination through registration completion.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Canadian Coverage - Content Left, Image Right */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-red-100 text-red-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Coast to Coast
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                National Service Coverage
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Professional trademark services available across all Canadian provinces and territories. Federal CIPO registration provides comprehensive nationwide protection through centralized application procedures.
              </p>
              
              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 mb-6">
                <h3 className="font-bold text-gray-900 mb-4 text-xl flex items-center">
                  <Globe className="w-6 h-6 text-red-600 mr-2" />
                  Provinces & Territories We Serve:
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">British Columbia</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Alberta</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Saskatchewan</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Manitoba</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Ontario</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Quebec</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Nova Scotia</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">New Brunswick</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">All Territories</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">And More!</span>
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-4 bg-brand-50 rounded-xl p-5">
                <TrendingUp className="w-6 h-6 text-brand-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Federal Registration</h4>
                  <p className="text-gray-600 text-sm">Single CIPO application establishes trademark rights across all Canadian provinces and territories under federal jurisdiction.</p>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-red-400 to-orange-400 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1534430480872-3498386e7856"
                alt="Canadian skyline"
                className="relative rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
              <div className="absolute -top-6 -right-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="text-4xl font-bold text-red-600 mb-1">10+</div>
                <div className="text-gray-700 font-semibold">Provinces Served</div>
                <div className="text-sm text-gray-500 mt-1">Coast to coast coverage</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-brand-600 to-brand-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Established Service Record
            </h2>
            <p className="text-xl text-brand-100">
              Professional results across Canadian trademark registrations
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">1000+</div>
              <div className="text-brand-100">Applications Filed</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">99%</div>
              <div className="text-brand-100">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">15+</div>
              <div className="text-brand-100">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">10</div>
              <div className="text-brand-100">Provinces Served</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSlider />

      <Footer />
    </div>
  );
};

export default About;
